import { useMemo } from 'react'
import { useGate } from 'gates'
import useStore from 'stores/utils/hooks/useStore'
import { PossibleVariants } from 'utils/tracking/optimizely/types'
import { resolveTieredVoucherAmount } from '../helpers/voucherAmount'
import {
  RCEVoucherPresentation,
  selectRCEVoucherPresentation
} from '../helpers/rceVoucherPresentation'
import { RCE_VOUCHER_CAMPAIGN_ID } from '../VoucherBanner.constants'
import useVoucherActivation from './useVoucherActivation'
import type { RCEVoucherExperimentVariant } from '../VoucherBanner.types'

interface UseRCEVoucherBannerReturn {
  presentation: RCEVoucherPresentation
  variant: RCEVoucherExperimentVariant
  amount: number | null
  isVoucherActivated: boolean
  activateVoucher: () => void
}

const useRCEVoucherBanner = (): UseRCEVoucherBannerReturn => {
  const isVariantB = useGate('rceVoucherVariantB')
  const isVariantC = useGate('rceVoucherVariantC')
  const {
    traversalSession,
    navigation: { currentPageInSession }
  } = useStore()
  const { isVoucherActivated, activateVoucher } = useVoucherActivation()

  const variant: RCEVoucherExperimentVariant = useMemo(() => {
    if (isVariantB) return PossibleVariants.VARIATION_B
    if (isVariantC) return PossibleVariants.VARIATION_C

    return PossibleVariants.CONTROL
  }, [isVariantB, isVariantC])

  const amount = resolveTieredVoucherAmount(
    traversalSession.rceEditInitialData.amount
  )
  const presentation = selectRCEVoucherPresentation({
    variant,
    page: currentPageInSession,
    amount,
    isActivated: isVoucherActivated
  })

  const activateRCEVoucher = () => {
    if (
      presentation.kind !== 'variant-b-edit' &&
      presentation.kind !== 'variant-c-edit'
    )
      return

    activateVoucher(RCE_VOUCHER_CAMPAIGN_ID)
  }

  return {
    presentation,
    variant,
    amount,
    isVoucherActivated,
    activateVoucher: activateRCEVoucher
  }
}

export default useRCEVoucherBanner
